import { useMemo, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useMutation } from "@apollo/client";
import { mdiClockOutline, mdiClose, mdiPencil, mdiPhone, mdiUpdate } from "@mdi/js";
import Icon from "@mdi/react";
import clsx from "clsx";
import dayjs from "dayjs";
import { useToggle } from "usehooks-ts";

import Button from "components/Button";
import ConfirmationModal from "components/ConfirmationModal";
import Select from "components/Select";
import { ArrowDotRightIcon } from "components/SVGIcons";
import { LOAD_STATUS_LIST } from "constants/loads";
import { routes } from "constants/routes";
import { MUTATION_DELETE_SAVED_LOAD } from "features/main/Home/SavedPosts/Loads/query";
import {
  convertDiffToAge,
  convertLoadData,
  convertToFullAddress,
  parseError
} from "helpers/convertFormData";
import { loadInfo, locationInfo } from "helpers/load";
import { showErrorMessage, showSuccessMessage } from "helpers/toastMessage";
import useDeviceType from "hooks/useDeviceType";
import { useUserFeature } from "hooks/useUserFeature";
import { IOption } from "models/Form";
import { IQueryLoadData } from "models/Load";
import { updateMyLoadAPI } from "services/api/load";
import { AppDispatch } from "store";
import { handleLoading } from "store/global/loader";
import { MUTATION_DELETE_LOAD } from "../../List/All/query";
import { MUTATION_UPDATE_LOAD_AGE } from "../../Post/Create/query";
import styles from "./style.module.scss";
import BidTableDetails from "../BidTableDetails";

interface IProps {
  data: IQueryLoadData;
  onEdit?: (id: string) => void;
  onClickDetails?: (id: string) => void;
  refresh?: VoidFunction;
  setIsRefresh?: React.Dispatch<React.SetStateAction<boolean>>;
  editable?: boolean;
  showFooter?: boolean;
  showStatus?: boolean;
  isSavedLoad?: boolean;
  questionRefresh?: VoidFunction;
}

export default function LoadPostedItem({
  data,
  onEdit,
  onClickDetails,
  refresh,
  setIsRefresh,
  editable = true,
  showFooter = true,
  showStatus = false,
  isSavedLoad = false,
  questionRefresh
}: Readonly<IProps>) {
  const { isDesktop } = useDeviceType();
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const { isBrokerSubscribed } = useUserFeature();
  const [loadStatus, setLoadStatus] = useState<IOption>();
  const [isHideBidList, setIsHideBidList] = useState(false);

  const [isUpdateStatusConfirmModalOpen, toggleUpdateStatusConfirmModal] = useToggle();
  const [isDeleteConfirmModalOpen, toggleDeleteConfirmModal] = useToggle();
  const [isRefreshAgeConfirmModalOpen, toggleRefreshAgeConfirmModal] = useToggle();
  const [isDeleteSavedLoadConfirmModalOpen, toggleDeleteSavedLoadConfirmModal] = useToggle();

  const loadData = useMemo(() => {
    if (!data) return null;
    return convertLoadData(data);
  }, [data]);

  const {
    id,
    timePosted,
    originLocations,
    userLoadNumber,
    targetRate,
    originAddress,
    originCity,
    originState,
    originCountry,
    destinationLocations,
    destinationAddress,
    destinationCity,
    destinationState,
    trailerType,
    length,
    weight,
    bids,
    timeDelivery: { deliveryDay, deliveryTime },
    timeStart: { startDay, startTime },
    timeEnd: { endDay, endTime },
    isDeleted,
    status,
    user: { contactPhone },
    fundingRequest
  } = loadData;

  const { control, setValue, handleSubmit } = useForm<{
    status: IOption;
  }>({
    values: {
      status: LOAD_STATUS_LIST.find((item) => item.value === status) ?? LOAD_STATUS_LIST[0]
    }
  });

  const onSubmit = () => {};

  const LoadAge = convertDiffToAge(dayjs().diff(dayjs(timePosted), "milliseconds"));

  const [updateLoadAge] = useMutation(MUTATION_UPDATE_LOAD_AGE, {
    variables: { id: id },
    onError(err) {
      err.graphQLErrors.map(({ message }) => showErrorMessage(parseError(message)));
    }
  });

  const [deleteLoad] = useMutation(MUTATION_DELETE_LOAD, {
    variables: { id: id },
    onError(err) {
      err.graphQLErrors.map(({ message }) => showErrorMessage(parseError(message)));
    }
  });

  const [deleteSavedLoad] = useMutation(MUTATION_DELETE_SAVED_LOAD, {
    variables: { loadId: id },
    onError(err) {
      err.graphQLErrors.map(({ message }) => showErrorMessage(parseError(message)));
    }
  });

  const updateMyLoadAge = async () => {
    try {
      dispatch(handleLoading(true));
      const { data } = await updateLoadAge({ variables: { id: id } });
      if (data.updateLoad.load) {
        toggleRefreshAgeConfirmModal();
        showSuccessMessage("Load deleted successfully.");
        refresh && refresh();
      } else {
        showErrorMessage("Failed to delete load.");
      }
    } finally {
      dispatch(handleLoading(false));
    }
  };

  const deleteMyLoad = async () => {
    try {
      dispatch(handleLoading(true));
      const { data } = await deleteLoad({ variables: { id: id } });
      if (data.deleteLoad.success) {
        if (setIsRefresh) setIsRefresh(true);
        toggleDeleteConfirmModal();
        if (refresh) refresh();
        showSuccessMessage("Load deleted successfully.");
      } else {
        showErrorMessage("Failed to delete load.");
      }
    } finally {
      dispatch(handleLoading(false));
    }
  };

  const deleteUserSavedLoad = async () => {
    try {
      dispatch(handleLoading(true));
      const { data } = await deleteSavedLoad({ variables: { loadId: id } });
      if (data.deleteUserSavedLoad.success) {
        if (setIsRefresh) setIsRefresh(true);
        toggleDeleteSavedLoadConfirmModal();
        if (refresh) refresh();
        showSuccessMessage("Load deleted successfully.");
      } else {
        showErrorMessage("Failed to delete load.");
      }
    } finally {
      dispatch(handleLoading(false));
    }
  };

  const updateLoadStatus = async () => {
    try {
      const selectedStatus = LOAD_STATUS_LIST.find(
        (load_status) => load_status.value === loadStatus?.value
      );
      dispatch(handleLoading(true));
      await updateMyLoadAPI(id, { status: selectedStatus?.status });
      setValue("status", loadStatus ?? LOAD_STATUS_LIST[0]);
      refresh && refresh();
    } catch {
      showErrorMessage("Failed to update load status.");
    } finally {
      dispatch(handleLoading(false));
      toggleUpdateStatusConfirmModal();
      setLoadStatus(undefined);
    }
  };

  function goToFundingRequest() {
    if (isBrokerSubscribed && fundingRequest) {
      navigate(`${routes.HAULPAY_BROKER_FUNDING_REQUEST}/${fundingRequest.id}`);
    } else if (isBrokerSubscribed) {
      navigate(routes.LOADS_CREATE_FUNDING_REQUEST, {
        state: {
          loadId: id,
          loadLength: length,
          userLoadNumber: userLoadNumber,
          targetRate: targetRate,
          isOriginLoad: true
        }
      });
    }
  }

  return (
    <div className="flex flex-col gap-3">
      <div
        className={clsx(styles.loadPostedItemContainer, editable && !isSavedLoad && styles.active)}>
        <div className={styles.loadPostedItemContent}>
          {editable && (
            <Button
              type="button"
              onClick={isSavedLoad ? toggleDeleteSavedLoadConfirmModal : toggleDeleteConfirmModal}
              className={styles.loadPostedItemClose}
              rounded
              iconButton>
              <Icon path={mdiClose} className="m-auto size-2.5" />
            </Button>
          )}

          <div className={styles.loadPostedItemHeader}>
            <div className={styles.description}>
              <span className="break-words">
                {locationInfo(originLocations).name +
                  convertToFullAddress(originAddress, originCity, originState, originCountry) +
                  locationInfo(originLocations).suffix}
              </span>
              <ArrowDotRightIcon className="flex-none w-fit" />
              <span className="break-words">
                {locationInfo(destinationLocations).name +
                  convertToFullAddress(
                    destinationAddress,
                    destinationCity,
                    destinationState,
                    originCountry
                  ) +
                  locationInfo(destinationLocations).suffix}
              </span>
            </div>
            <span className="text-xs desktop:text-sm text-label">
              {loadInfo(trailerType, length, weight)}
            </span>
            {editable && <span className="text-xs desktop:text-sm">Load #: {userLoadNumber}</span>}
          </div>

          <ul className={styles.loadPostedItemCardBox}>
            <li>
              <span className="text-xs text-label">Ready</span>
              <span>{startDay}</span>
              <span className="text-xs">{startTime}</span>
            </li>

            <li>
              <span className="text-xs text-label">Close</span>
              <span>{endDay}</span>
              <span className="text-xs">{endTime}</span>
            </li>

            <li>
              <span className="text-xs text-label">Deliver by</span>
              <span>{deliveryDay === "Invalid Date" ? "--" : deliveryDay}</span>
              <span className="text-xs">
                {deliveryTime === "Invalid Date" ? "--" : deliveryTime}
              </span>
            </li>

            <li className="text-right">
              <span className={styles.loadPostedItemCardIcon}>
                <Icon path={mdiClockOutline} className="size-3" />
                &nbsp;Load Age
              </span>
              <span>{LoadAge}</span>
            </li>
          </ul>
          {showFooter && (
            <div className={styles.loadPostedItemFooter}>
              {!isSavedLoad && editable ? (
                <>
                  <Button
                    type="button"
                    className={styles.loadPostedItemFooterButton}
                    rounded
                    iconButton
                    onClick={() => onEdit && onEdit(id)}>
                    <Icon path={mdiPencil} className="m-auto" />
                  </Button>
                  {!isDeleted ? (
                    <Button
                      type="button"
                      className={styles.loadPostedItemFooterButton}
                      rounded
                      iconButton
                      onClick={toggleRefreshAgeConfirmModal}>
                      <Icon path={mdiUpdate} className="m-auto" />
                    </Button>
                  ) : null}
                </>
              ) : (
                <a href={`tel:+1${contactPhone}`} className={styles.callButton}>
                  <Icon path={mdiPhone} className="m-auto" />
                </a>
              )}

              <Button
                type="button"
                variant="outline"
                rounded
                onClick={() => onClickDetails && onClickDetails(id)}>
                Details
              </Button>
            </div>
          )}
        </div>

        {editable || showStatus ? (
          <form
            className="flex flex-col pt-2 border-t-2 border-subtitle"
            onSubmit={handleSubmit(onSubmit)}>
            <div className={styles.loadPostedItemStatus}>
              <Controller
                name="status"
                control={control}
                render={({ field: { value = LOAD_STATUS_LIST[0] } }) => (
                  <Select
                    options={LOAD_STATUS_LIST}
                    value={value}
                    onChange={(value) => {
                      setLoadStatus(value);
                      toggleUpdateStatusConfirmModal();
                    }}
                    btnClass={clsx("!p-3 !pr-10 !w-1/2 desktop:!w-48", isSavedLoad && "!flex-none")}
                    disabled={isDeleted}
                  />
                )}
              />

              {bids && bids.length > 0 && isDesktop ? (
                <button
                  type="button"
                  className="font-semibold text-info"
                  onClick={() => setIsHideBidList(!isHideBidList)}>
                  <span>{isHideBidList ? "Hide " : "View "}</span>
                  <span>{`(${bids.length}) Pending Bids`}</span>
                </button>
              ) : null}

              {isBrokerSubscribed && !isSavedLoad && (
                <button
                  type="button"
                  className={styles.loadPostedItemStatusButton}
                  onClick={goToFundingRequest}>
                  {fundingRequest ? "View Request Payment" : "$ Request Payment"}
                </button>
              )}
            </div>

            {bids && bids.length > 0 && !isDesktop ? (
              <button
                type="button"
                className="mx-auto mt-3 font-semibold text-info"
                onClick={() => setIsHideBidList(!isHideBidList)}>
                <span>{isHideBidList ? "Hide " : "View "}</span>
                <span>{`(${bids.length}) Pending Bids`}</span>
              </button>
            ) : null}

            <ConfirmationModal
              isConfirmModalOpen={isUpdateStatusConfirmModalOpen}
              toggleConfirmModal={toggleUpdateStatusConfirmModal}
              content={`Are you sure you want to update this load’s status to "${loadStatus?.label}"?`}
              labelCancelButton="Cancel"
              labelOKButton="Confirm"
              onOK={updateLoadStatus}
            />

            <ConfirmationModal
              isConfirmModalOpen={isDeleteConfirmModalOpen}
              toggleConfirmModal={toggleDeleteConfirmModal}
              title="Confirm Load Removal"
              content={
                <div>
                  <p>{`Are you sure you would like to remove your load form ${originCity}, ${originState} to ${destinationCity}, ${destinationState}?`}</p>
                  <p>This load post will be moved to history and removed from this view.</p>
                  <p>{`You posted this on ${dayjs(timePosted).format("dddd, MMMM D, YYYY HH:mm A")}`}</p>
                </div>
              }
              labelOKButton="Remove"
              onOK={deleteMyLoad}
              isHideHeaderIcon
            />

            <ConfirmationModal
              isConfirmModalOpen={isDeleteSavedLoadConfirmModalOpen}
              toggleConfirmModal={toggleDeleteSavedLoadConfirmModal}
              content="Are you sure you want to remove this load from saved?"
              onOK={deleteUserSavedLoad}
            />
          </form>
        ) : null}
      </div>

      {bids && bids.length > 0 && isHideBidList && questionRefresh ? (
        <div className="mobile:flex mobile:flex-col mobile:gap-3 mobile:text-sm">
          <BidTableDetails bids={bids} isDeleted={isDeleted} refresh={refresh} />
        </div>
      ) : null}

      <ConfirmationModal
        isConfirmModalOpen={isRefreshAgeConfirmModalOpen}
        toggleConfirmModal={toggleRefreshAgeConfirmModal}
        title="Refresh Age of Post"
        content="Are you sure want to refresh the age of this post to show up newer to other users?"
        labelOKButton="Refresh"
        onOK={updateMyLoadAge}
      />
    </div>
  );
}
